import { createTheme } from '@mui/material/styles';

// Premium color palette centered around #FF8B93
const palette = {
    primary: {
        main: '#FF8B93',
        light: '#FFB4B9',
        dark: '#E67078',
        contrastText: '#FFFFFF',
    },
    secondary: {
        main: '#2D3436',
        light: '#636E72',
        dark: '#1E2427',
        contrastText: '#FFFFFF',
    },
    background: {
        default: '#FAFAFA',
        paper: '#FFFFFF',
        subtle: '#F5F5F5',
    },
    text: {
        primary: '#1A1A1A',
        secondary: '#636E72',
        disabled: '#B2BEC3',
    },
    divider: 'rgba(0, 0, 0, 0.06)',
    action: {
        hover: 'rgba(255, 139, 147, 0.04)',
        selected: 'rgba(255, 139, 147, 0.08)',
        focus: 'rgba(255, 139, 147, 0.12)',
    },
};

// Consistent spacing scale (base unit: 8px)
const spacing = 8;

// Typography scale for elegance
const typography = {
    fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"SF Pro Display"',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
    ].join(','),
    h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
    },
    h2: {
        fontSize: '2rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
    },
    h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.4,
    },
    h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        letterSpacing: '-0.005em',
        lineHeight: 1.4,
    },
    h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.5,
    },
    h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
    },
    subtitle1: {
        fontSize: '0.9375rem',
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: '0.01em',
    },
    subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
    },
    body1: {
        fontSize: '0.9375rem',
        lineHeight: 1.6,
    },
    body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
    },
    caption: {
        fontSize: '0.75rem',
        lineHeight: 1.5,
        letterSpacing: '0.02em',
    },
    button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '0.02em',
        textTransform: 'none',
    },
};

// Component overrides for premium feel
const components = {
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                backgroundColor: palette.background.default,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(0, 0, 0, 0.2)',
                },
            },
        },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                borderRadius: '8px',
                padding: '10px 20px',
                boxShadow: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(255, 139, 147, 0.25)',
                    transform: 'translateY(-1px)',
                },
            },
            contained: {
                '&:hover': {
                    boxShadow: '0 6px 16px rgba(255, 139, 147, 0.3)',
                },
            },
            outlined: {
                borderWidth: '1.5px',
                '&:hover': {
                    borderWidth: '1.5px',
                    backgroundColor: 'rgba(255, 139, 147, 0.04)',
                },
            },
        },
    },
    MuiCard: {
        styleOverrides: {
            root: {
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                    transform: 'translateY(-2px)',
                },
            },
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                borderRadius: '10px',
                boxShadow: 'none',
                border: '1px solid rgba(0, 0, 0, 0.06)',
            },
            elevation1: {
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            },
        },
    },
    MuiAppBar: {
        styleOverrides: {
            root: {
                boxShadow: '0 1px 0 rgba(0, 0, 0, 0.06)',
            },
        },
    },
    MuiToolbar: {
        styleOverrides: {
            root: {
                minHeight: '64px !important',
                '@media (min-width: 600px)': {
                    minHeight: '72px !important',
                },
            },
        },
    },
    MuiInputBase: {
        styleOverrides: {
            root: {
                fontSize: '0.9375rem',
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: '10px',
                transition: 'all 0.2s ease-in-out',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.primary.light,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1.5px',
                    borderColor: palette.primary.main,
                },
            },
            notchedOutline: {
                borderColor: 'rgba(0, 0, 0, 0.1)',
            },
        },
    },
    MuiSelect: {
        styleOverrides: {
            select: {
                padding: '12px 14px',
            },
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                fontSize: '0.9375rem',
                '&.Mui-focused': {
                    color: palette.primary.main,
                },
            },
        },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: '6px',
                fontWeight: 500,
            },
        },
    },
    MuiAvatar: {
        styleOverrides: {
            root: {
                border: '2px solid rgba(255, 255, 255, 0.9)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            },
        },
    },
    MuiListItemButton: {
        styleOverrides: {
            root: {
                borderRadius: '10px',
                margin: '4px 8px',
                padding: '10px 16px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: 'rgba(255, 139, 147, 0.06)',
                },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 139, 147, 0.1)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 139, 147, 0.14)',
                    },
                },
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: {
                fontSize: '1.125rem',
                fontWeight: 600,
                padding: '20px 24px 12px',
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                padding: '16px 24px',
            },
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: {
                padding: '12px 24px 20px',
            },
        },
    },
    MuiTabs: {
        styleOverrides: {
            root: {
                minHeight: '48px',
            },
            indicator: {
                height: '3px',
                borderRadius: '3px 3px 0 0',
                backgroundColor: palette.primary.main,
            },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: {
                minHeight: '48px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9375rem',
                color: palette.text.secondary,
                '&.Mui-selected': {
                    color: palette.primary.main,
                },
            },
        },
    },
    MuiTextField: {
        styleOverrides: {
            root: {
                '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                },
            },
        },
    },
    MuiPopover: {
        styleOverrides: {
            paper: {
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
                border: '1px solid rgba(0, 0, 0, 0.04)',
            },
        },
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
                marginTop: '8px',
            },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                padding: '10px 16px',
                borderRadius: '6px',
                margin: '2px 8px',
                '&:hover': {
                    backgroundColor: 'rgba(255, 139, 147, 0.06)',
                },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 139, 147, 0.1)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 139, 147, 0.14)',
                    },
                },
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: {
                borderRadius: '10px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: 'rgba(255, 139, 147, 0.08)',
                },
            },
        },
    },
    MuiAlert: {
        styleOverrides: {
            root: {
                borderRadius: '10px',
            },
        },
    },
    MuiBackdrop: {
        styleOverrides: {
            root: {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(4px)',
            },
        },
    },
};

// Shadows for depth
const shadows = [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.04)',
    '0 1px 3px rgba(0, 0, 0, 0.06)',
    '0 2px 4px rgba(0, 0, 0, 0.06)',
    '0 4px 6px rgba(0, 0, 0, 0.06)',
    '0 6px 8px rgba(0, 0, 0, 0.06)',
    '0 8px 12px rgba(0, 0, 0, 0.08)',
    '0 10px 16px rgba(0, 0, 0, 0.08)',
    '0 12px 20px rgba(0, 0, 0, 0.08)',
    '0 14px 24px rgba(0, 0, 0, 0.08)',
    '0 16px 28px rgba(0, 0, 0, 0.1)',
    '0 18px 32px rgba(0, 0, 0, 0.1)',
    '0 20px 36px rgba(0, 0, 0, 0.1)',
    '0 22px 40px rgba(0, 0, 0, 0.1)',
    '0 24px 44px rgba(0, 0, 0, 0.12)',
    '0 26px 48px rgba(0, 0, 0, 0.12)',
    '0 28px 52px rgba(0, 0, 0, 0.12)',
    '0 30px 56px rgba(0, 0, 0, 0.12)',
    '0 32px 60px rgba(0, 0, 0, 0.14)',
    '0 34px 64px rgba(0, 0, 0, 0.14)',
    '0 36px 68px rgba(0, 0, 0, 0.14)',
    '0 38px 72px rgba(0, 0, 0, 0.14)',
    '0 40px 76px rgba(0, 0, 0, 0.16)',
    '0 42px 80px rgba(0, 0, 0, 0.16)',
    '0 44px 84px rgba(0, 0, 0, 0.16)',
];

export const theme = createTheme({
    palette,
    typography,
    spacing,
    components,
    shadows,
    shape: {
        borderRadius: 10,
    },
});

// Export design tokens for custom usage
export const designTokens = {
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '48px',
    },
    borderRadius: {
        sm: '6px',
        md: '10px',
        lg: '12px',
        xl: '16px',
    },
    transition: {
        fast: '0.15s ease-in-out',
        normal: '0.2s ease-in-out',
        slow: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    colors: palette,
};
